/**
 * Service layer — the AB 253 review clock (api/permit-review-clocks.js, database/329,
 * docs/PLAN_CHECK_PERFORMANCE.md "The review clock").
 *
 * Health & Safety Code 17960.3: once the city deems a residential building permit application
 * complete, it has 30 business days to determine compliance (or must estimate longer). When the
 * 30 run out, or an estimate over 30 arrives, the owner may retain a private plan checker and must
 * notify the city within FIVE business days. `clockState` is the one place that reading lives.
 */
import { busDays, addBusDays, todayIso } from './planCheckPerformance.js'

export const CLOCK_DAYS = 30
export const NOTICE_DAYS = 5

async function call(body) {
  const headers = { 'Content-Type': 'application/json' }
  try { if (window.__clerkGetToken) { const t = await window.__clerkGetToken(); if (t) headers['Authorization'] = 'Bearer ' + t } } catch (_) { /* server rejects */ }
  const res = await fetch('/api/permit-review-clocks', { method: 'POST', headers, body: JSON.stringify(body) })
  const j = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(j.error || 'Review clock request failed (' + res.status + ')')
  return j
}

// reviewId → clock | null (null = loaded, none set). One shared map so every surface agrees.
const _clocks = new Map()
let _allLoaded = false
const changed = () => window.dispatchEvent(new Event('review-clocks-changed'))

/** undefined = not loaded yet; null = loaded, no clock set. */
export const cachedClock = id => _clocks.has(id) ? _clocks.get(id) : (_allLoaded ? null : undefined)

/** Load clocks for these reviews (or every clock when `reviewIds` is omitted). */
export async function loadReviewClocks(reviewIds) {
  if (!reviewIds && _allLoaded) return
  const want = reviewIds ? reviewIds.filter(id => !_clocks.has(id)) : null
  if (want && !want.length) return
  const { clocks } = await call({ action: 'list', reviewIds: want || undefined })
  if (want) want.forEach(id => _clocks.set(id, null))
  for (const c of clocks) _clocks.set(c.reviewId, c)
  if (!reviewIds) _allLoaded = true
  changed()
}

export async function saveReviewClock(reviewId, fields) {
  const { clock } = await call({ action: 'save', reviewId, ...fields })
  _clocks.set(reviewId, clock || null)
  changed()
  return clock
}

export function suggestDeemedComplete(reviewId) {
  return call({ action: 'suggest', reviewId })
}

const fmt = s => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? `${+m[2]}/${+m[3]}` : '' }

/**
 * Where a clock stands today.
 * state: 'unset' | 'running' | 'window' | 'lapsed' | 'stopped' | 'notified'
 * tone:  'muted' | 'calm' | 'soon' | 'urgent' | 'done'
 */
export function clockState(clock, today = todayIso()) {
  if (!clock || !clock.deemedCompleteOn) return { state: 'unset', tone: 'muted', label: 'Start the clock' }
  const start = clock.deemedCompleteOn
  const end = clock.stoppedOn && clock.stoppedOn < today ? clock.stoppedOn : today
  const day = busDays(start, end)
  const base = { day, of: CLOCK_DAYS, start, windowOpensOn: addBusDays(start, CLOCK_DAYS), windowEndsOn: addBusDays(start, CLOCK_DAYS + NOTICE_DAYS) }
  // An estimate over 30 business days opens the window early: five business days from receipt.
  if (clock.estimateBusinessDays > CLOCK_DAYS && clock.estimateReceivedOn) {
    const estEnd = addBusDays(clock.estimateReceivedOn, NOTICE_DAYS)
    if (estEnd < base.windowEndsOn) { base.windowOpensOn = clock.estimateReceivedOn; base.windowEndsOn = estEnd; base.trigger = 'estimate' }
  }
  base.trigger = base.trigger || '30 days'
  if (clock.noticeSentOn) return { ...base, state: 'notified', tone: 'done', label: 'Notice sent ' + fmt(clock.noticeSentOn) }
  if (clock.stoppedOn && clock.stoppedOn <= today) return { ...base, state: 'stopped', tone: 'done', label: `City answered on day ${day}` }
  if (today > base.windowEndsOn) return { ...base, state: 'lapsed', tone: 'muted', label: 'Notice window closed ' + fmt(base.windowEndsOn) }
  if (today >= base.windowOpensOn && (base.trigger === 'estimate' || day > CLOCK_DAYS)) {
    const left = busDays(today, base.windowEndsOn) + 1   // today counts
    return { ...base, state: 'window', tone: 'urgent', left, label: left > 1 ? `Notice window · ${left} days left` : 'Notice window · last day' }
  }
  const left = CLOCK_DAYS - day
  return { ...base, state: 'running', tone: left <= 5 ? 'soon' : 'calm', left, label: `Day ${day} of ${CLOCK_DAYS}` }
}

/** The review statuses during which a first review is with the city (where the clock matters). */
export const UNDER_REVIEW_STATUSES = new Set(['Submitted - Intake in Process', 'Under Review'])

/**
 * Whether a review with no clock should invite one: a building department review that is under
 * review now and was submitted in the last six months (older "Under Review" rows are records nobody
 * closed out, not live reviews).
 */
export function shouldPromptClock(review) {
  if (!review || !UNDER_REVIEW_STATUSES.has(review.status)) return false
  if (!/building/i.test((review.agencyType || '') + ' ' + (review.agency || ''))) return false
  const sub = review.dateSubmitted
  if (!sub) return true
  const d = new Date(String(sub).slice(0, 10) + 'T12:00:00'); const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() - 6)
  return d >= cutoff
}
